import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type HTMLAttributes, type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

export const TAG_COLORS = ["red", "yellow", "green", "blue", "gray"] as const;
export type TagColor = (typeof TAG_COLORS)[number];

const colorClasses: Record<TagColor, string> = {
	red: "bg-intent-danger-bg border-intent-danger-border text-intent-danger-text",
	yellow: "bg-intent-warning-bg border-intent-warning-border text-intent-warning-text",
	green: "bg-intent-success-bg border-intent-success-border text-intent-success-text",
	blue: "bg-intent-info-bg border-intent-info-border text-intent-info-text",
	gray: "bg-intent-neutral-bg border-intent-neutral-border text-intent-neutral-text",
};

const tagVariants = cva(
	"inline-flex w-fit max-w-full shrink-0 items-center whitespace-nowrap rounded-full border px-2 text-[12px] leading-[16px] font-medium",
	{
		variants: { color: colorClasses },
		defaultVariants: { color: "gray" },
	},
);

export type TagProps = HTMLAttributes<HTMLSpanElement> &
	VariantProps<typeof tagVariants> & {
		ref?: Ref<HTMLSpanElement>;
		trackingId?: string;
		testId?: string;
	};

/**
 * A small status pill, toned from semantic tokens. Display only: reach for `ActionChip` or
 * `RadioChip` when the pill is clickable. The legacy `ui/Tag` stays for its existing callers.
 */
export const Tag: FC<TagProps> = ({
	color,
	className,
	trackingId,
	testId = "tag",
	ref,
	...rest
}) => (
	<span
		ref={ref}
		className={cn(tagVariants({ color }), className)}
		data-testid={testId}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Tag.displayName = "Tag";
